// Banner globale: avvisa il NUOVO assegnatario quando gli viene assegnato/riassegnato un cliente.
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, UserPlus, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { listAssignmentNotifications, ackAssignmentNotification } from "@/lib/clientAssignmentsApi";

export function ClientAssignmentBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    if (!user || user.role === "client_user") return;
    try {
      setItems(await listAssignmentNotifications());
    } catch {
      /* silenzioso: il banner è best-effort */
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!items.length) return null;

  const dismiss = async (nid) => {
    setItems((prev) => prev.filter((n) => n.id !== nid));
    try { await ackAssignmentNotification(nid); } catch { /* ignore */ }
  };

  const open = (n) => {
    dismiss(n.id);
    navigate(`/clienti/${n.client_id}/dettaglio`);
  };

  return (
    <div className="space-y-1.5 border-b border-border/60 bg-background/80 px-4 py-2 backdrop-blur-xl sm:px-6" data-testid="client-assignment-banner">
      {items.slice(0, 4).map((n) => (
        <div
          key={n.id}
          className="flex items-center gap-2.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm"
          data-testid={`client-assignment-row-${n.id}`}
        >
          <UserPlus className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
          <span className="min-w-0 flex-1 truncate">
            <span className="font-medium text-sky-700 dark:text-sky-400">Cliente assegnato a te:</span>{" "}
            <span className="text-foreground">{n.client_name}</span>
            <span className="text-muted-foreground"> · da {n.assigned_by_name}</span>
          </span>
          <button
            type="button"
            onClick={() => open(n)}
            className="flex shrink-0 items-center gap-1 text-xs font-medium text-sky-700 hover:underline dark:text-sky-400"
            data-testid={`client-assignment-open-${n.id}`}
          >
            Apri <ArrowRight className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => dismiss(n.id)}
            className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
            title="Ho preso visione"
            data-testid={`client-assignment-dismiss-${n.id}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      {items.length > 4 && (
        <div className="px-1 text-xs text-muted-foreground" data-testid="client-assignment-more">
          + altri {items.length - 4} clienti assegnati
        </div>
      )}
    </div>
  );
}
